// Mesquite Mafia: Audio Engine & SFX Synthesizer
import { hapticImpact } from '../native/capacitor.js';

export let sfxMuted = false;
export let bgmMuted = false;
export let sfxVolume = 0.65;
export let bgmVolume = 0.55;

export const bgmPlaylist = [
  {
    id: 'unfortunate_soul',
    title: 'Unfortunate Soul',
    artist: 'MrsMaryMack',
    file: 'audio/unfortunate_soul.mp3',
    tag: 'Noir Anthem'
  },
  {
    id: 'mesquite_hustle',
    title: 'Mesquite Hustle',
    artist: 'Syndicate Beats',
    file: 'audio/mesquite_hustle.mp3',
    fallbackFile: 'audio/Mesquite Hustle.wav',
    tag: 'Texas Trap Combat'
  }
];
export let currentBGMIndex = 0;

export const sfxMap = {
  city_select: 'audio/sfx/city_select.wav',
  city_select_alt: 'audio/sfx/city_select_alt.wav',
  click: 'audio/sfx/ui_click.wav',
  tab_switch: 'audio/sfx/tab_switch.wav',
  march_dispatch: 'audio/sfx/march_dispatch.wav',
  cash: 'audio/sfx/cash_register.wav',
  vault_equip: 'audio/sfx/vault_equip.wav',
  lounge_favor: 'audio/sfx/lounge_favor.wav',
  hit: 'audio/sfx/combat_hit.wav',
  shield: 'audio/sfx/combat_shield.wav',
  tick: 'audio/sfx/tick.wav'
};

export const sfxAudioCache = {};
for (const [key, path] of Object.entries(sfxMap)) {
  try {
    const audio = new Audio(path);
    audio.preload = 'auto';
    sfxAudioCache[key] = audio;
  } catch (e) {}
}

export function playSFX(name) {
  if (sfxMuted) return;
  try {
    // Native haptic feedback trigger
    if (name === 'hit' || name === 'march_dispatch') {
      hapticImpact('heavy');
    } else if (name === 'cash' || name === 'vault_equip') {
      hapticImpact('medium');
    } else if (name === 'click') {
      hapticImpact('light');
    }

    const cached = sfxAudioCache[name];
    if (cached) {
      const sound = cached.cloneNode ? cached.cloneNode() : cached;
      sound.volume = sfxVolume;
      sound.play().catch(() => {});
    } else if (sfxMap[name]) {
      const sound = new Audio(sfxMap[name]);
      sound.volume = sfxVolume;
      sound.play().catch(() => {});
    }
  } catch (err) {}
}

export const SFX = {
  citySelect: (alt = false) => playSFX(alt ? 'city_select_alt' : 'city_select'),
  citySelectAlt: () => playSFX('city_select_alt'),
  click: () => playSFX('click'),
  tab: () => playSFX('tab_switch'),
  march: () => playSFX('march_dispatch'),
  cash: () => playSFX('cash'),
  gunshot: () => playSFX('hit'),
  hit: () => playSFX('hit'),
  shield: () => playSFX('shield'),
  vault: () => playSFX('vault_equip'),
  lounge: () => playSFX('lounge_favor'),
  radio: () => playSFX('tick'),
  tick: () => playSFX('tick')
};

export function loadBGMTrack(index, autoPlay = true) {
  currentBGMIndex = (index + bgmPlaylist.length) % bgmPlaylist.length;
  const track = bgmPlaylist[currentBGMIndex];
  const player = document.getElementById('bgm-player');
  if (!player) return;
  
  player.src = track.file;
  player.volume = bgmVolume;
  player.load();

  if (autoPlay && !bgmMuted) {
    player.play().then(() => {
      updateMusicUI(true);
    }).catch(() => {
      if (track.fallbackFile) {
        player.src = track.fallbackFile;
        player.load();
        player.play().then(() => updateMusicUI(true)).catch(() => updateMusicUI(false));
      } else {
        updateMusicUI(false);
      }
    });
  } else {
    updateMusicUI(!player.paused);
  }
}

export function startBGM() {
  const player = document.getElementById('bgm-player');
  if (!player || bgmMuted) return;
  if (!player.src || player.src === '' || player.src.endsWith('undefined')) {
    loadBGMTrack(currentBGMIndex, true);
    return;
  }
  player.volume = bgmVolume;
  player.play().then(() => updateMusicUI(true)).catch(() => {
    loadBGMTrack(currentBGMIndex, true);
  });
}

export function toggleBGM() {
  const player = document.getElementById('bgm-player');
  if (!player) return;
  if (player.paused) {
    bgmMuted = false;
    startBGM();
  } else {
    player.pause();
    updateMusicUI(false);
  }
}

export function nextBGMTrack() {
  playSFX('click');
  const player = document.getElementById('bgm-player');
  const wasPlaying = player ? !player.paused : true;
  loadBGMTrack(currentBGMIndex + 1, wasPlaying);
  const track = bgmPlaylist[currentBGMIndex];
  if (window.log) window.log(`♫ Syndicate Radio: Switched track to "${track.title}" (${track.artist})`);
}

export function toggleSFX() {
  sfxMuted = !sfxMuted;
  const btn = document.getElementById('sfx-btn-icon');
  if (btn) btn.innerText = sfxMuted ? '🔇 SFX OFF' : '🔊 SFX ON';
  if (!sfxMuted) playSFX('click');
}

export function updateMusicUI(isPlaying) {
  const track = bgmPlaylist[currentBGMIndex] || bgmPlaylist[0];
  const btnIcon = document.getElementById('music-btn-icon');
  const btnTxt = document.getElementById('music-btn-txt');
  const startIcon = document.getElementById('start-screen-music-icon');
  const startTxt = document.getElementById('start-screen-music-txt');
  const startSub = document.getElementById('start-screen-music-sub');
  
  if (btnIcon && btnTxt) {
    btnIcon.innerText = isPlaying ? '⏸' : '🎵';
    btnTxt.innerText = isPlaying ? `"${track.title}"` : `Play "${track.title}"`;
  }
  if (startIcon && startTxt) {
    startIcon.innerText = isPlaying ? '⏸' : '▶';
    startTxt.innerText = isPlaying 
      ? `Now Playing: "${track.title}" — ${track.artist}` 
      : `Theme: "${track.title}" — ${track.artist} (Click to Play)`;
  }
  if (startSub) {
    startSub.innerText = isPlaying 
      ? `Playing ${track.tag} • Click to Pause` 
      : `Click to play or Enter Compound to launch`;
  }
}

export function playVoiceBark(characterId) {
  playSFX('city_select_alt');
}

// Auto-advance playlist when track finishes
if (typeof window !== 'undefined') {
  window.addEventListener('DOMContentLoaded', () => {
    const bgmElem = document.getElementById('bgm-player');
    if (bgmElem) {
      bgmElem.addEventListener('ended', () => {
        loadBGMTrack(currentBGMIndex + 1, true);
      });
    }
  });
}
